
import Button from 'components/base/Button';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProfileData, UserData } from './components/DataDefinitions';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider'
import UserLoginInfo from './components/UserInfo';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import PersonalInfo from './components/PersonalInfo';

const breadcrumbItems: PageBreadcrumbItem[] = [
    {
        label: 'Dashboard',
        url: '/dashboard/home'
    },
    {
        label: 'Users (Admin)',
        url: '/admin/users'
    },
    {
        label: 'Create (Admin)',
        active: true
    }
];

const AdminUserProfileAdd = () => {

    const { setFooterClass } = useMainLayoutContext();
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const auth = useAuth();
    const navigate = useNavigate();

    // Form validation
    const [errors, setErrors] = useState<{
        login?: string, password_mismatch?: string,     // userinfo validation
    }>({});

    const [profileErrors, setProfileErrors] = useState<{
        email?: string, first_name?: string; last_name?: string;
    }>({});


    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [user, setUser] = useState<UserData>({
        user_id: 0, login: '', first_name: '', last_name: '',
        disabled: false,
        customer: { customer_id: 0, customer_name: '' },
        role: 'user', inserted_at: '',
        password: '', confirmPassword: ''
    });

    const [profile, setProfile] = useState<ProfileData>({
        profile_id: 0,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        picture: null,  // base64,
        location: '',

        company_department: '',
        linkedin_url: '',

        is_coach: 'off',
        is_mentor: 'off',

        about_me: '',
        inserted_at: '',
        contacts_count: 0,
        coach_types: '',
        mentor_types: '',

        question1: '', question2: '', question3: '',
        preferences: {
            careerCoaching: false,
            internalMobility: false,
            leadershipCoaching: false,
            mentoringLeadership: false,
            mentoringNewPosition: false,
            mentoringSales: false,
            shadowMentoring: false,
            teamCoaching: false,
        },
        interests: {
            art: false,
            cinemaTheatre: false,
            creativeWriting: false,
            cooking: false,
            reading: false,
            sport: false,
            technology: false,
            traveling: false,
            yogaMeditation: false,
        },
        services: '',
        coaching_method: ''
    });

    const handleSubmit = async (event: any) => {
        console.log("handleSubmit");
        if (validateUserForm() === false) {
            console.log("invalid form");
            event.preventDefault();
            event.stopPropagation();
            return
        }

        const formData = new FormData();
        formData.append('userForm', JSON.stringify(user));
        formData.append('profileForm', JSON.stringify(profile));
        try {
            const response = await apiClient.post(
                `/v1/admin/users`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            setToastMessage(response.data['message'])
            setShow(true);
            navigate("/admin/users");
        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        }

    };

    const validateUserForm = (): boolean => {
        const newErrors: { login?: string; password_mismatch?: string } = {};
        const newProfileErrors: { email?: string; first_name?: string; last_name?: string; } = {};
        let isValid = true;
        if (!user.login) {
            newErrors.login = 'Email is required';
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(user.login)) {
            newErrors.login = 'Email is not valid';
            isValid = false;
        }

        // Validate first/last name
        if (!profile.first_name || profile.first_name.trim() === '') {
            newProfileErrors.first_name = 'First name is required';
            isValid = false;
        }
        if (!profile.last_name || profile.last_name.trim() === '') {
            newProfileErrors.last_name = 'Last name is required';
            isValid = false;
        }

        if (user.password != user.confirmPassword || user.password.trim() == '') {
            newErrors.password_mismatch = 'Password & confirm password must match and being not empty';
            isValid = false;
        }
        // Set any errors in the state
        setErrors(newErrors);
        setProfileErrors(newProfileErrors);
        return isValid;
    };

    useEffect(() => {
        setFooterClass('d-none d-lg-block');
        return () => {
            setFooterClass('');
        };

    }, []);


    const handleUserChange = (name: string, value: any) => {
        console.log(name + " " + value);
        setUser({
            ...user, [name]: value
        });
    };

    const handleProfileChange = (name: string, value: any) => {
        setProfile({
            ...profile, [name]: value
        });
    };


    if (loading) {
        return <div>Loading...</div>;
    }


    return (
        <>
            <PageBreadcrumb items={breadcrumbItems} />
            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => { setShow(false); }} show={show} delay={5000} autohide>
                        <Toast.Body>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>

            <div className="mb-1">
                <UserLoginInfo user={user} onUserChange={handleUserChange} isAdd={true} errors={errors} />

                <PersonalInfo className="mt-2 mb-2" profile={profile} onProfileChange={handleProfileChange} errors={profileErrors} />

                <div className="text-end">
                    <Button variant="warning" className="me-2" onClick={() => navigate("/admin/users")}>
                        {t("UserProfile.cancel_changes")}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} >{t("UserProfile.save_information")}</Button>
                </div>
            </div>
        </>
    );
};

export default AdminUserProfileAdd;
